import useSWR from 'swr';
import type { OrganizationToken, NetworkType } from '@mls-wallet/shared-types';
import { useMyOrganization } from './use-my-organization';
import denApi from '@/lib/api/denApi';

/**
 * Fetcher function to get organization tokens from the API
 */
const fetcher = async (url: string): Promise<OrganizationToken[]> => {
  return await denApi.get<OrganizationToken[]>(url);
};

interface UseOrganizationTokensOptions {
  networkId: NetworkType | null;
  accountIds?: string[] | undefined;
}

/**
 * Hook to fetch tokens available in the organization for a specific network.
 * Optionally filter by specific account IDs.
 * Returns tokens from accounts that have addresses on the specified network.
 */
export function useOrganizationTokens({
  networkId,
  accountIds,
}: UseOrganizationTokensOptions) {
  const { organization } = useMyOrganization();

  const shouldFetch = organization?.id && networkId;

  // Build URL with query params
  let url: string | null = null;
  if (shouldFetch) {
    const params = new URLSearchParams({ networkId: String(networkId) });
    if (accountIds && accountIds.length > 0) {
      accountIds.forEach((id) => params.append('accountIds', id));
    }
    // Org context comes from x-current-org-id cookie (not URL params)
    url = `organization/tokens?${params.toString()}`;
  }

  const { data, error, isLoading } = useSWR<OrganizationToken[]>(url, fetcher);
  const hasSuccessfulResponse = data !== undefined && error === undefined;

  return {
    tokens: data ?? [],
    isLoading,
    error,
    hasSuccessfulResponse,
  };
}
